import { useEffect, useRef } from "react";

type Props = { progressRef: React.MutableRefObject<number> };

type Particle = {
  radius: number;
  phase: number;
  opField: number;
  // home position (normalized 0..1) — the anchor the particle drifts around
  hx: number;
  hy: number;
  // current position (px)
  x: number;
  y: number;
  // velocity (px/s)
  vx: number;
  vy: number;
  // per-particle wave params
  waveAmpX: number;
  waveAmpY: number;
  waveFreq: number;
  // parallax factor (depth-like)
  depth: number;
};

export default function ParticleRibbon({ progressRef: _progressRef }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let dpr = window.devicePixelRatio || 1;
    let width = 0;
    let height = 0;
    let particles: Particle[] = [];

    const isMobile = () => window.innerWidth < 640;
    const isTablet = () => window.innerWidth >= 640 && window.innerWidth < 1024;

    const initParticles = () => {
      particles = [];
      if (isMobile()) return;
      const count = isTablet() ? 180 : 320;

      const bandCount = 4;
      const bandCenters: number[] = [];
      for (let b = 0; b < bandCount; b++) {
        bandCenters.push(0.15 + (b / (bandCount - 1)) * 0.7);
      }

      for (let i = 0; i < count; i++) {
        const roll = Math.random();
        const isAccent = roll < 0.05;
        const isMain = roll < 0.55;
        let radius: number;
        if (isAccent) radius = 4.5 + Math.random() * 1.5;
        else if (isMain) radius = 2 + Math.random() * 2;
        else radius = 1.5 + Math.random() * 1.5;

        const opField = isAccent
          ? 0.20 + Math.random() * 0.12
          : isMain
          ? 0.11 + Math.random() * 0.11
          : 0.05 + Math.random() * 0.07;

        const bandIdx = Math.floor(Math.random() * bandCount);
        const bandY = bandCenters[bandIdx];
        const spread = 0.10 + Math.random() * 0.06;
        const hy = Math.min(0.98, Math.max(0.02, bandY + (Math.random() - 0.5) * 2 * spread));
        const hx = Math.random();

        particles.push({
          radius,
          phase: Math.random() * Math.PI * 2,
          opField,
          hx,
          hy,
          x: hx * window.innerWidth,
          y: hy * window.innerHeight,
          vx: 0,
          vy: 0,
          waveAmpX: 14 + Math.random() * 22,
          waveAmpY: 8 + Math.random() * 16,
          waveFreq: 0.15 + Math.random() * 0.30,
          depth: 0.6 + Math.random() * 0.8,
        });
      }
    };

    const resize = () => {
      dpr = window.devicePixelRatio || 1;
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (particles.length === 0) initParticles();
      // re-anchor current positions to new viewport if empty velocity
      for (const p of particles) {
        if (!isFinite(p.x)) p.x = p.hx * width;
        if (!isFinite(p.y)) p.y = p.hy * height;
      }
    };

    resize();
    window.addEventListener("resize", resize);

    // Scroll impulse: adds lingering velocity to the whole field
    let scrollImpulse = 0; // signed, decays over time (px/s worth of push)
    let lastScrollY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const dy = y - lastScrollY;
      lastScrollY = y;
      // Add to impulse — this decays gradually so motion "coasts"
      scrollImpulse += -dy * 0.9; // scroll down (dy>0) pushes particles up
      // Clamp so wild flings don't explode
      const cap = 260;
      if (scrollImpulse > cap) scrollImpulse = cap;
      if (scrollImpulse < -cap) scrollImpulse = -cap;
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    let activeAlpha = 0;
    let lastTs = performance.now();
    let raf = 0;
    const start = performance.now();

    const draw = () => {
      raf = requestAnimationFrame(draw);
      const now = performance.now();
      const dt = Math.min(0.05, (now - lastTs) / 1000);
      lastTs = now;

      ctx.clearRect(0, 0, width, height);
      if (isMobile() || particles.length === 0) return;

      // Fade based on Section 04 (practice) entering viewport
      const practice = document.getElementById("practice");
      let activeTarget = 1;
      if (practice) {
        const pr = practice.getBoundingClientRect();
        const fadeStart = height * 0.80;
        const fadeEnd = height * 0.20;
        if (pr.top <= fadeEnd) activeTarget = 0;
        else if (pr.top >= fadeStart) activeTarget = 1;
        else activeTarget = (pr.top - fadeEnd) / (fadeStart - fadeEnd);
      }
      const hero = document.querySelector<HTMLElement>('[data-slot="hero"]');
      if (hero) {
        const hr = hero.getBoundingClientRect();
        if (hr.bottom < -height) activeTarget = 0;
      }

      const aRate = activeTarget > activeAlpha ? 4.0 : 2.5;
      activeAlpha += (activeTarget - activeAlpha) * Math.min(1, dt * aRate);

      // Slow decay on scroll impulse => inertia / coasting
      // Lower factor = longer glide.
      const impulseDecay = Math.exp(-dt * 0.9);
      scrollImpulse *= impulseDecay;

      if (activeAlpha <= 0.005) return;

      const time = (now - start) / 1000;

      // Global wave phase — like ocean swell, always moving
      // Two overlapping low-freq oscillators create irregular natural rhythm
      const swellY = Math.sin(time * 0.35) * 0.6 + Math.sin(time * 0.19 + 1.2) * 0.4;
      const swellX = Math.sin(time * 0.28 + 0.7) * 0.6 + Math.sin(time * 0.13 + 2.1) * 0.4;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        const homeX = p.hx * width;
        const homeY = p.hy * height;

        // Target position = home + organic wave offsets (ocean-like)
        // Use spatial phase so particles form flowing bands rather than sync
        const spatial = p.hx * 4.2 + p.hy * 3.1;
        const waveOffX =
          Math.sin(time * p.waveFreq + p.phase + spatial * 0.8) * p.waveAmpX +
          swellX * 10 * p.depth;
        const waveOffY =
          Math.cos(time * p.waveFreq * 0.85 + p.phase * 1.2 + spatial * 0.6) * p.waveAmpY +
          swellY * 14 * p.depth;

        // Scroll impulse: adds lingering displacement, particles coast after scroll
        // Depth causes parallax variation across particles
        const targetX = homeX + waveOffX;
        const targetY = homeY + waveOffY + scrollImpulse * 0.35 * p.depth;

        // Spring-toward-target with damping => produces inertia / smooth easing
        // Softer spring = more inertia / longer coast
        const stiffness = 3.5; // 1/s^2 scale
        const damping = 2.2;   // 1/s
        const ax = (targetX - p.x) * stiffness - p.vx * damping;
        const ay = (targetY - p.y) * stiffness - p.vy * damping;

        p.vx += ax * dt;
        p.vy += ay * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;

        const finalOp = p.opField * activeAlpha;
        if (finalOp <= 0.008) continue;

        const breath = 0.9 + 0.2 * (0.5 + 0.5 * Math.sin(time * 1.1 + p.phase));
        const r = p.radius * breath;

        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(2, 157, 201, ${Math.min(1, finalOp).toFixed(3)})`;
        ctx.fill();
      }
    };

    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 35 }}
      aria-hidden="true"
    />
  );
}